import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { fetchBudgetsV2, fetchBudgetV2StatusCounts, fetchBudgetUserProfile } from "../../../../api/fetchBudgetV2";

import { getRouteByProfile } from "../utils/utils";
import { getKanbanRouteByProfile } from "../utils/utils";
import { getKanbanColumns, IKanbanColumn } from "../utils/columns";

import {
  BudgetKanbanStatusFilter,
  IBudgetStatusCounts,
  IBudgetKanbanFilters,
  IBudget,
} from "../../../../models/budgetV2";
import { BudgetStatusFilterEnum } from "../../enums/budgetStatus";
import { toLocalDateTimeString } from "../../../../utils/dateUtils";

const emptyBudgetStatusCounts: IBudgetStatusCounts = {
  sent: 0,
  altered: 0,
  counterproposal: 0,
  approved: 0,
  rejected: 0,
};

const isValidStatusFilter = (value: string | null): value is BudgetKanbanStatusFilter =>
  value !== null && value in BudgetStatusFilterEnum;

const getFiltersFromSearchParams = (searchParams: URLSearchParams): IBudgetKanbanFilters => ({
  page: 1,
  limit: 20,
  status: isValidStatusFilter(searchParams.get("status"))
    ? searchParams.get("status") as BudgetKanbanStatusFilter
    : undefined,
  startUpdatedAt: searchParams.get("startUpdatedAt")
    ? toLocalDateTimeString(searchParams.get("startUpdatedAt")!)
    : undefined,
  endUpdatedAt: searchParams.get("endUpdatedAt")
    ? toLocalDateTimeString(searchParams.get("endUpdatedAt")!, true)
    : undefined,
  chassis: searchParams.get("chassis") || undefined,
});

const buildSearchParamsFromFilters = (filters: IBudgetKanbanFilters): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.status) params.set("status", filters.status);
  if (filters.startUpdatedAt) params.set("startUpdatedAt", toLocalDateTimeString(filters.startUpdatedAt));
  if (filters.endUpdatedAt) params.set("endUpdatedAt", toLocalDateTimeString(filters.endUpdatedAt, true));
  if (filters.chassis) params.set("chassis", filters.chassis);

  return params;
};

export interface IKanbanColumnData {
  budgets: IBudget[];
  total: number;
  page: number;
  isLastPage: boolean;
  loadingMore: boolean;
}

const emptyColumnData: IKanbanColumnData = { budgets: [], total: 0, page: 1, isLastPage: true, loadingMore: false };

export const useBudgetKanban = (persona?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<IBudgetKanbanFilters>(() =>
    getFiltersFromSearchParams(searchParams)
  );
  const columns = useMemo(() => getKanbanColumns(persona), [persona]);
  const [statusCounts, setStatusCounts] = useState<IBudgetStatusCounts>(emptyBudgetStatusCounts);
  const [columnsData, setColumnsData] = useState<Record<string, IKanbanColumnData>>({});
  // ignores responses of outdated requests (e.g. filters changed while loading)
  const requestId = useRef(0);
  const [viewType, setViewType] = useState<"geral" | "cliente" | "fabricante">("geral");
  const [loading, setLoading] = useState<boolean>(true);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [selectedBudget, setSelectedBudget] = useState<IBudget | null>(null);
  
  const navigate = useNavigate();

  const handleCreateBudget = async () => {
    const userProfile = await fetchBudgetUserProfile();
    const createRoute = getRouteByProfile(userProfile);
    const kanbanRoute = getKanbanRouteByProfile(userProfile);
    const redirectBack = encodeURIComponent(kanbanRoute);
    navigate(`${createRoute}/create?redirectBack=${redirectBack}`);
  };

  const getStatusCounts = async (filtersToUse: IBudgetKanbanFilters) => {
    const statusCountsResponse = await fetchBudgetV2StatusCounts(filtersToUse);
    setStatusCounts(statusCountsResponse);
  };

  // each column is paginated on its own, so cards of a column are always appended to the end of that column
  const getBudgets = async (filtersToUse: IBudgetKanbanFilters) => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setColumnsData({});

    const responses = await Promise.all(
      columns.map((column) => fetchBudgetsV2({ ...filtersToUse, column: column.column, page: 1 })),
    );
    if (currentRequest !== requestId.current) return;

    setColumnsData(
      Object.fromEntries(
        columns.map((column, index) => [
          column.key,
          { budgets: responses[index].content, total: responses[index].totalElements, page: 1, isLastPage: responses[index].last, loadingMore: false },
        ]),
      ),
    );
    setLoading(false);
  };

  const loadMoreColumn = async (column: IKanbanColumn) => {
    const columnKey = column.key;
    const data = columnsData[columnKey];
    if (!data || data.isLastPage || data.loadingMore || loading) return;

    const currentRequest = requestId.current;
    const nextPage = data.page + 1;
    setColumnsData((prev) => ({ ...prev, [columnKey]: { ...prev[columnKey], loadingMore: true } }));

    const response = await fetchBudgetsV2({ ...filters, column: column.column, page: nextPage });
    if (currentRequest !== requestId.current) return;

    setColumnsData((prev) => {
      const current = prev[columnKey] ?? emptyColumnData;
      const knownIds = new Set(current.budgets.map((budget) => budget.uuid));
      return {
        ...prev,
        [columnKey]: {
          budgets: [...current.budgets, ...response.content.filter((budget) => !knownIds.has(budget.uuid))],
          total: response.totalElements,
          page: nextPage,
          isLastPage: response.last,
          loadingMore: false,
        },
      };
    });
  };

  // a single "load more" for the whole board: fetches the next page of every column that still has budgets
  const loadMoreBudgets = async () => {
    await Promise.all(columns.map(loadMoreColumn));
  };

  useEffect(() => {
    const nextSearchParams = buildSearchParamsFromFilters(filters);
    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [filters.status, filters.startUpdatedAt, filters.endUpdatedAt, filters.chassis]);

  useEffect(() => {
    getStatusCounts(filters);
    getBudgets(filters);
  }, [filters.status, filters.startUpdatedAt, filters.endUpdatedAt, filters.chassis]);

  const handleViewVersions = (budget: IBudget) => {
    setSelectedBudget(budget);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedBudget(null);
  };

  return {
    loading,
    columns,
    columnsData,
    statusCounts,
    filters,
    viewType,

    setFilters,
    setViewType,
    loadMoreBudgets,

    showHistoryModal,
    selectedBudget,

    handleCreateBudget,
    handleViewVersions,
    handleCloseHistoryModal,
  };
};
